import { useEffect, useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { useDropdown } from '../hooks/useDropdown';
import {
	StyledArrow,
	StyledContainer,
	StyledDropdown,
	StyledDropdownItem,
	StyledDropdownList,
	StyledInputWrapper,
	StyledSearchInput,
	StyledSearchWrapper,
} from './Dropdown.styled';

interface SearchableSelectOption {
	value: string;
	label: string;
}

interface SearchableSelectProps {
	options: SearchableSelectOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	filterPlaceholder?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Выберите...',
	filterPlaceholder = 'Поиск...',
}) => {
	const [debouncedFilter, setDebouncedFilter] = useState('');

	const {
		isOpen,
		filterText,
		filteredOptions: rawFilteredOptions,
		containerRef,
		inputRef,
		listRef,
		selectedOption,
		handleToggle,
		handleSelect,
		handleInputChange,
		handleInputKeyDown,
	} = useDropdown(options, value, onChange);

	// Debounce filter text for better performance with large lists
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setDebouncedFilter(filterText);
		}, 200);
		return () => clearTimeout(timeoutId);
	}, [filterText]);

	// Apply debounced filtering
	const filteredOptions = useMemo(() => {
		if (!debouncedFilter.trim()) {
			return rawFilteredOptions;
		}
		const lowerFilter = debouncedFilter.toLowerCase();
		return rawFilteredOptions.filter(
			(opt) =>
				opt.value.toLowerCase().includes(lowerFilter) ||
				opt.label.toLowerCase().includes(lowerFilter),
		);
	}, [rawFilteredOptions, debouncedFilter]);

	// Add sound to handleSelect
	const handleSelectWithSound = (option: SearchableSelectOption) => {
		playSound(Sound.LinkCursor);
		handleSelect(option);
	};

	const handleToggleWithSound = () => {
		playSound(Sound.LinkCursor);
		handleToggle();
	};

	// Auto-scroll to selected item when dropdown opens
	useEffect(() => {
		if (isOpen && value && listRef?.current) {
			const selectedElement = listRef.current.querySelector(
				`[data-selected="true"]`,
			) as HTMLElement;
			if (selectedElement) {
				selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
			}
		}
	}, [isOpen, value, filteredOptions]);

	const currentDisplay = selectedOption?.label || value || placeholder;

	return (
		<StyledContainer ref={containerRef}>
			<StyledInputWrapper $focus={isOpen} onClick={handleToggleWithSound}>
				<Text
					style={{
						flex: 1,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{currentDisplay}
				</Text>
				<StyledArrow $active={isOpen}>▼</StyledArrow>
			</StyledInputWrapper>
			{isOpen && (
				<StyledDropdown>
					<StyledDropdownList ref={listRef}>
						{filteredOptions.length === 0 ? (
							<StyledDropdownItem disabled>
								<Text style={{ opacity: 0.5 }}>Ничего не найдено</Text>
							</StyledDropdownItem>
						) : (
							filteredOptions.map((option) => (
								<StyledDropdownItem
									key={option.value}
									$selected={option.value === value}
									data-selected={option.value === value}
									onClick={(e) => {
										e.stopPropagation();
										handleSelectWithSound(option);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<Text>{option.label}</Text>
									{option.value && (
										<Text style={{ opacity: 0.5, fontSize: 12, marginLeft: 8 }}>
											({option.value})
										</Text>
									)}
								</StyledDropdownItem>
							))
						)}
					</StyledDropdownList>
					<StyledSearchWrapper>
						<StyledSearchInput
							ref={inputRef}
							value={filterText}
							onChange={handleInputChange}
							onKeyDown={handleInputKeyDown}
							placeholder={filterPlaceholder}
							autoFocus
						/>
					</StyledSearchWrapper>
				</StyledDropdown>
			)}
		</StyledContainer>
	);
};
